/**
 * Tasks view - full CRUD with priority filtering, completion toggle, 44px touch targets.
 * Uses virtual scrolling to prevent DOM bloat with large task lists (500+ items).
 */

import { useState, useEffect } from 'react';
import type { Task, CreateTaskInput } from '@anna-calendar/shared';
import { useTasksStore } from '../store/tasks';
import { TaskModal } from '../components/TaskModal';
import { VirtualList } from '../components/VirtualList';
import { formatTime, priorityLabel, priorityColor } from '../utils/dates';

/** Fixed row height for virtual scrolling (accommodates title + metadata + padding). */
const TASK_ROW_HEIGHT = 88;

export function TasksView() {
  const { tasks, fetchTasks, createTask, updateTask, deleteTask } = useTasksStore();
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filterPriority, setFilterPriority] = useState<number | null>(null);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const filtered = tasks
    .filter((t) => filterPriority === null || t.priority === filterPriority)
    .sort((a, b) => {
      // Incomplete first, then by priority desc, then due date
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      if (a.priority !== b.priority) return b.priority - a.priority;
      return (a.due_date || 'zz').localeCompare(b.due_date || 'zz');
    });

  const handleCreate = async (data: CreateTaskInput) => {
    await createTask(data);
  };

  const handleUpdate = async (data: CreateTaskInput) => {
    if (!editingTask) return;
    await updateTask(editingTask.id, data);
    setEditingTask(null);
  };

  const handleToggle = async (id: string, completed: number) => {
    await updateTask(id, { completed: completed ? 0 : 1 });
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this task?')) {
      await deleteTask(id);
    }
  };

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  return (
    <div className="h-full overflow-y-auto p-4 desktop-pb">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Tasks
        </h2>
        <button
          onClick={() => { setEditingTask(null); setShowModal(true); }}
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{ backgroundColor: 'var(--accent)', color: '#fff', minHeight: '44px' }}
        >
          + New Task
        </button>
      </div>

      {/* Priority filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setFilterPriority(null)}
          className="px-3 py-2 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: filterPriority === null ? 'var(--accent)' : 'var(--bg-tertiary)',
            color: filterPriority === null ? '#fff' : 'var(--text-secondary)',
            minHeight: '44px',
          }}
        >
          All
        </button>
        {[0, 1, 2].map((p) => (
          <button
            key={p}
            onClick={() => setFilterPriority(filterPriority === p ? null : p)}
            className="px-3 py-2 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: filterPriority === p ? 'var(--accent)' : 'var(--bg-tertiary)',
              color: filterPriority === p ? '#fff' : 'var(--text-secondary)',
              minHeight: '44px',
            }}
          >
            {priorityLabel(p)}
          </button>
        ))}
      </div>

      {/* Task list with virtual scrolling */}
      <VirtualList
        items={filtered}
        itemHeight={TASK_ROW_HEIGHT}
        keyExtractor={(t) => t.id}
        emptyMessage={filterPriority !== null ? 'No tasks with this priority' : 'No tasks yet'}
        className="flex-1"
        renderItem={(task) => (
          <div
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{ backgroundColor: 'var(--bg-tertiary)', marginBottom: '8px' }}
          >
            {/* Completion toggle */}
            <button
              onClick={() => handleToggle(task.id, task.completed)}
              className="w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{
                borderColor: task.completed ? 'var(--success)' : 'var(--border-color)',
                backgroundColor: task.completed ? 'var(--success)' : 'transparent',
                minHeight: '44px',
                minWidth: '44px',
                width: '44px',
                height: '44px',
              }}
              aria-label={task.completed ? 'Mark incomplete' : 'Mark complete'}
            >
              {task.completed ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : null}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0" onClick={() => openEdit(task)} style={{ cursor: 'pointer' }}>
              <p
                className="text-base"
                style={{
                  color: task.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                  textDecoration: task.completed ? 'line-through' : 'none',
                }}
              >
                {task.title}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {task.due_date && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-muted)' }}>
                    {task.due_date}
                  </span>
                )}
                {task.due_time && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatTime(task.due_time)}</span>
                )}
                <span
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--bg-primary)', color: priorityColor(task.priority) }}
                >
                  {priorityLabel(task.priority)}
                </span>
                {task.recurrence_rule && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--accent)' }}>
                    Recurring
                  </span>
                )}
              </div>
            </div>

            {/* Delete */}
            <button
              onClick={() => handleDelete(task.id)}
              className="p-2 rounded-lg flex-shrink-0"
              style={{ color: 'var(--danger)', minHeight: '44px', minWidth: '44px' }}
              aria-label="Delete task"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )}
      />

      {/* Task modal */}
      <TaskModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={editingTask ? handleUpdate : handleCreate}
        task={editingTask}
      />
    </div>
  );
}
